import React from 'react';
import { useAdminPermissions } from '@/hooks/admin/useAdminPermissions';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { PermissionType, RoleType } from '@/lib/rbac';

interface PermissionGuardProps {
    children: React.ReactNode;
    /** Single permission required */
    permission?: PermissionType | string;
    /** Multiple permissions - user must have ALL if requireAll is true, ANY otherwise */
    permissions?: (PermissionType | string)[];
    /** If true, user must have ALL permissions. If false, ANY permission suffices */
    requireAll?: boolean;
    /** Specific roles that are allowed */
    roles?: (RoleType | string)[];
    /** Content to show when access is denied */
    fallback?: React.ReactNode;
    /** If true, shows loading state while checking permissions */
    showLoading?: boolean;
    /** Custom loading component */
    loadingComponent?: React.ReactNode;
    /** If true, returns null instead of fallback on access denied */
    hideOnDenied?: boolean;
}

const DefaultLoadingComponent = () => (
    <div className="flex justify-center items-center p-8">
        <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-600">Checking permissions...</p>
        </div>
    </div>
);

const DefaultAccessDenied = () => (
    <Card className="max-w-md mx-auto mt-8">
        <CardHeader className="text-center">
            <CardTitle className="text-red-600">Access Denied</CardTitle>
            <CardDescription>
                You don't have permission to view this content
            </CardDescription>
        </CardHeader>
        <CardContent className="text-center">
            <button
                onClick={() => window.history.back()}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
                Go Back
            </button>
        </CardContent>
    </Card>
);

export const PermissionGuard: React.FC<PermissionGuardProps> = ({
    children,
    permission,
    permissions = [],
    requireAll = false,
    roles,
    fallback,
    showLoading = true,
    loadingComponent,
    hideOnDenied = false,
}) => {
    const { permissions: userPermissions, loading } = useAdminPermissions();

    // Show loading state
    if (loading && showLoading) {
        return <>{loadingComponent || <DefaultLoadingComponent />}</>;
    }

    // Check role-based access first
    if (roles && roles.length > 0) {
        if (!roles.includes(userPermissions.role)) {
            if (hideOnDenied) return null;
            return <>{fallback || <DefaultAccessDenied />}</>;
        }
    }

    // Collect all required permissions
    const requiredPerms = permission ? [permission, ...permissions] : permissions;

    // If no specific permissions required, allow access (role check passed or no role check)
    if (requiredPerms.length === 0) {
        return <>{children}</>;
    }

    // Check permissions
    const hasAccess = requireAll
        ? requiredPerms.every(p => userPermissions.permissions.includes(p))
        : requiredPerms.some(p => userPermissions.permissions.includes(p));

    if (!hasAccess) {
        if (hideOnDenied) return null;
        return <>{fallback || <DefaultAccessDenied />}</>;
    }

    return <>{children}</>;
};

export default PermissionGuard;
